// src/pages/Admin/Cities/AdminClubCreate.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/utils/axios";

import cls from "./AdminClubCreate.module.scss";

const STATUSES = ["pending", "open", "closed"];

const AdminClubCreate = () => {
  const navigate = useNavigate();
  const { cityId, streetId } = useParams();

  const [streetMeta, setStreetMeta] = useState(null);

  const [clubNo, setClubNo] = useState("");
  const [clubName, setClubName] = useState("");
  const [status, setStatus] = useState("pending");
  const [clubVibe, setClubVibe] = useState("");
  const [clubIntro, setClubIntro] = useState("");
  const [clubDescription, setClubDescription] = useState("");
  const [sortOrder, setSortOrder] = useState(0);

  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleBack = () => navigate(-1);

  useEffect(() => {
    const loadStreet = async () => {
      try {
        const { data } = await api.get(
          `/v1/admin/cities/${cityId}/streets/${streetId}`
        );
        setStreetMeta(data || null);
      } catch (e) {
        console.error("Failed to load street", e);
      }
    };
    if (cityId && streetId) loadStreet();
  }, [cityId, streetId]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess("");

    if (!clubName.trim()) {
      setErr("Club name is required.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        clubName: clubName.trim(),
        status, // pending/open/closed
        clubVibe,
        clubIntro,
        clubDescription,
        sortOrder: Number(sortOrder) || 0,
        // clubNo is optional (Number | null)
        clubNo:
          clubNo === "" || clubNo === null
            ? null
            : Number.isFinite(Number(clubNo))
            ? Number(clubNo)
            : null,
      };

      const { data } = await api.post(
        `/v1/admin/cities/${cityId}/streets/${streetId}/clubs`,
        payload
      );

      const id = data?._id;
      setSuccess("Club created. Redirecting…");

      navigate(
        id
          ? `/admin/cities/single/${cityId}/streets/${streetId}/clubs/${id}`
          : `/admin/cities/single/${cityId}/streets/${streetId}/clubs`
      );
    } catch (e) {
      console.error("Create club failed", e);
      const msg =
        e?.response?.data?.message || e?.message || "Failed to create club.";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cls.page}>
      {/* HEADER */}
      <div className={cls.header}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          ← Back
        </button>

        <h1 className={cls.title}>Create Club</h1>
        <p className={cls.subtitle}>
          You are creating a club inside:
          <br />
          <strong>{streetMeta?.streetName || "Loading street…"}</strong>
        </p>
      </div>

      {/* META BAR */}
      <div className={cls.metaBar}>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Mode</span>
          <span className={cls.metaValue}>Create new club</span>
        </div>

        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Language</span>
          <span className={cls.metaValue}>
            {streetMeta?.streetLanguage?.toUpperCase?.() || "…"}
          </span>
        </div>

        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Status</span>
          <span className={cls.metaValue}>{status}</span>
        </div>

        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Club No</span>
          <span className={cls.metaValue}>{clubNo || "—"}</span>
        </div>
      </div>

      {err && <div className={cls.error}>⚠ {err}</div>}
      {success && <div className={cls.success}>{success}</div>}

      <div className={cls.layout}>
        {/* LEFT CARD – IMAGES (locked until created) */}
        <section className={cls.avatarCard}>
          <div className={cls.avatarHeader}>
            <h2 className={cls.cardTitle}>Club Images</h2>
            <p className={cls.cardSubtitle}>
              After creating the club, you can upload avatar, thumb, and
              desktop/mobile images on its detail page.
            </p>
          </div>

          <div className={cls.avatarPreviewWrap}>
            <div className={cls.avatarGlow}>
              <div className={cls.avatarFallback}>No avatar yet</div>
            </div>
            <p className={cls.avatarMeta}>
              Uploads become available after saving.
            </p>
          </div>

          <div className={cls.imagesGrid}>
            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Avatar</p>
              <div className={cls.imagePreviewMini}>
                <div className={cls.miniFallback}>Pending</div>
              </div>
            </div>

            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Avatar Thumb</p>
              <div className={cls.imagePreviewMini}>
                <div className={cls.miniFallback}>Pending</div>
              </div>
            </div>

            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Desktop Images</p>
              <div className={cls.imagePreviewMini}>
                <div className={cls.miniFallback}>Pending</div>
              </div>
            </div>

            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Mobile Images</p>
              <div className={cls.imagePreviewMini}>
                <div className={cls.miniFallback}>Pending</div>
              </div>
            </div>
          </div>
        </section>

        {/* RIGHT CARD – FORM */}
        <section className={cls.formCard}>
          <div className={cls.formHeader}>
            <h2 className={cls.cardTitle}>Club Details</h2>
            <p className={cls.cardSubtitle}>
              Use only fields that exist in the Club model.
            </p>
          </div>

          <form className={cls.form} onSubmit={onSubmit}>
            {/* Club No + Status */}
            <div className={cls.formRow}>
              <div className={cls.field}>
                <label className={cls.label}>Club No (optional)</label>
                <input
                  type="number"
                  className={cls.input}
                  value={clubNo}
                  onChange={(e) => setClubNo(e.target.value)}
                  placeholder="101"
                />
                <p className={cls.help}>Leave empty to keep it null.</p>
              </div>

              <div className={cls.field}>
                <label className={cls.label}>Status</label>
                <select
                  className={cls.select}
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Name */}
            <div className={cls.field}>
              <label className={cls.label}>Club name</label>
              <input
                type="text"
                className={cls.input}
                value={clubName}
                onChange={(e) => setClubName(e.target.value)}
                placeholder="Velvet Lounge"
                required
              />
            </div>

            {/* Sort */}
            <div className={cls.field}>
              <label className={cls.label}>Sort order</label>
              <input
                type="number"
                className={cls.input}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
              <p className={cls.help}>Lower appears earlier.</p>
            </div>

            {/* Vibe */}
            <div className={cls.field}>
              <label className={cls.label}>Vibe / tagline</label>
              <input
                type="text"
                className={cls.input}
                value={clubVibe}
                onChange={(e) => setClubVibe(e.target.value)}
                placeholder="Intimate, bold, magnetic…"
              />
            </div>

            {/* Intro */}
            <div className={cls.field}>
              <label className={cls.label}>Intro</label>
              <textarea
                rows={3}
                className={cls.input}
                value={clubIntro}
                onChange={(e) => setClubIntro(e.target.value)}
                placeholder="Short intro shown first."
              />
            </div>

            {/* Description */}
            <div className={cls.field}>
              <label className={cls.label}>Description</label>
              <textarea
                rows={4}
                className={cls.input}
                value={clubDescription}
                onChange={(e) => setClubDescription(e.target.value)}
                placeholder="Longer explanation of the club."
              />
            </div>

            {/* ACTIONS */}
            <div className={cls.actions}>
              <button
                type="button"
                className={cls.secondaryBtn}
                onClick={handleBack}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className={cls.primaryBtn}
                type="submit"
                disabled={loading}
              >
                {loading ? "Creating…" : "Create Club"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default AdminClubCreate;
